import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import OwnerLayout from "@/components/OwnerLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, Wallet, Hourglass, Plus, ArrowRight, CalendarPlus, Clock, Star } from "lucide-react";
import StatCard from "@/components/common/StatCard";
import StatusBadge from "@/components/common/StatusBadge";
import EmptyState from "@/components/common/EmptyState";
import PageHeader from "@/components/common/PageHeader";
import { resolveBusinessConfig } from "@/config/businessTypes";
import { COMMERCIAL, formatPriceLabel, daysUntil } from "@/config/commercial";

// (StatCard extraído a /components/common/StatCard.jsx)

function NewAppointmentDialog({ business, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_phone: "", date: "", time: "", service_id: "" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/appointments", form);
      toast.success("Cita creada");
      setOpen(false);
      setForm({ client_name: "", client_phone: "", date: "", time: "", service_id: "" });
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="new-appointment-btn"
          className="bg-gold text-void hover:bg-gold/90 rounded-full font-bold h-12 px-6"
        >
          <Plus className="w-4 h-4 mr-2" /> Nueva cita manual
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-gold/20 text-neutral-100">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Nueva cita</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Nombre de la clienta</Label>
            <Input
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              className="mt-2 bg-void border-gold/20"
              data-testid="apt-client-name"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">WhatsApp</Label>
            <Input
              value={form.client_phone}
              onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
              placeholder="+527297553698"
              className="mt-2 bg-void border-gold/20"
              data-testid="apt-client-phone"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Fecha</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-2 bg-void border-gold/20"
                data-testid="apt-date"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Hora</Label>
              <Input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="mt-2 bg-void border-gold/20"
                data-testid="apt-time"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Servicio</Label>
            <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
              <SelectTrigger className="mt-2 bg-void border-gold/20" data-testid="apt-service">
                <SelectValue placeholder="Elige un servicio" />
              </SelectTrigger>
              <SelectContent className="bg-surface border-gold/20">
                {(business?.services || []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} — ${s.price} · {s.duration} min
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={submit}
            disabled={loading || !form.client_name || !form.client_phone || !form.date || !form.time || !form.service_id}
            className="w-full bg-gold text-void hover:bg-gold/90 rounded-full font-bold"
            data-testid="apt-submit-btn"
          >
            {loading ? "Guardando…" : "Guardar cita"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState({ today_count: 0, revenue_today: 0, pending: 0 });
  const [today, setToday] = useState([]);
  const [business, setBusiness] = useState(null);

  const load = async () => {
    try {
      const [s, apts, biz] = await Promise.all([
        api.get("/appointments/stats/today"),
        api.get("/appointments"),
        api.get("/business/me"),
      ]);
      setStats(s.data);
      setBusiness(biz.data);
      const t = new Date().toISOString().slice(0, 10);
      setToday(apts.data.filter((a) => a.date === t));
    } catch (e) {
      // handled by axios interceptor if needed
    }
  };

  useEffect(() => {
    load();
  }, []);

  const term = resolveBusinessConfig(business).terminology;

  return (
    <OwnerLayout>
      <PageHeader
        overline="Panel del negocio"
        title="Hoy en"
        highlight={business?.name || "tu negocio"}
        testId="dashboard-title"
      >
        <NewAppointmentDialog business={business} onCreated={load} />
      </PageHeader>

      {/* Trial / Subscription card */}
      {business && (() => {
        const days = daysUntil(business.subscription_expires_at);
        if (days === null) return null;
        const isFounding = !!business.founding_member;
        return (
          <div
            data-testid="trial-card"
            className="bg-surface gold-border rounded-2xl p-5 md:p-6 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 animate-fade-up"
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-11 h-11 rounded-full bg-void/60 border border-gold/30 flex items-center justify-center shrink-0">
                {isFounding ? (
                  <Star className="w-5 h-5 text-gold" strokeWidth={1.5} />
                ) : (
                  <Clock className="w-5 h-5 text-cyan" strokeWidth={1.5} />
                )}
              </div>
              <div className="min-w-0">
                <p className="overline mb-1">
                  {isFounding ? (
                    <span className="text-gold" data-testid="founding-badge">⭐ Founding Member</span>
                  ) : (
                    "Tu prueba termina en"
                  )}
                </p>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-display text-3xl md:text-4xl" style={{ color: days <= 3 ? "#FF3AD2" : "#FFD700" }}>
                    {days}
                  </span>
                  <span className="text-sm text-neutral-400">{days === 1 ? "día" : "días"}</span>
                  <span className="text-xs text-neutral-500 ml-2">· {COMMERCIAL.planName} {formatPriceLabel()}</span>
                </div>
              </div>
            </div>
            <a
              href={`https://wa.me/527297553698?text=${encodeURIComponent(`Hola, quiero administrar la suscripción de ${business.name}`)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="manage-subscription-btn"
            >
              <Button variant="outline" className="border-cyan/40 text-cyan hover:bg-cyan/10 rounded-full">
                Administrar suscripción
              </Button>
            </a>
          </div>
        );
      })()}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10">
        <StatCard label={`${term.appointmentPlural.charAt(0).toUpperCase() + term.appointmentPlural.slice(1)} de hoy`} value={stats.today_count} icon={CalendarDays} color="#FFD700" testId="stat-today-count" />
        <StatCard label="Cobrado hoy" value={`$${stats.revenue_today.toLocaleString("es-MX")}`} icon={Wallet} color="#00E5FF" testId="stat-revenue-today" />
        <StatCard label="Pendiente" value={`$${stats.pending.toLocaleString("es-MX")}`} icon={Hourglass} color="#FF3AD2" testId="stat-pending" />
      </div>

      <div className="flex items-end justify-between mb-4">
        <h2 className="font-display text-2xl capitalize">{term.appointmentPlural} de hoy</h2>
        <Link
          to="/dashboard/appointments"
          data-testid="see-all-appointments-link"
          className="text-xs uppercase tracking-widest text-cyan hover:text-gold"
        >
          Ver todas <ArrowRight className="inline w-3 h-3 ml-1" />
        </Link>
      </div>

      <div className="bg-surface gold-border rounded-2xl overflow-hidden" data-testid="today-list">
        {today.length === 0 ? (
          <EmptyState
            icon={CalendarPlus}
            title={`Sin ${term.appointmentPlural} hoy`}
            description={`Crea una manualmente o comparte tu link público para que tus ${term.clientPlural} reserven.`}
            testId="today-empty"
          />
        ) : (
          <div className="divide-y divide-gold/10">
            {today.map((a) => (
              <div key={a.id} className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <div className="font-mono text-cyan text-lg w-16">{a.time}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{a.client_name}</div>
                  <div className="text-xs text-neutral-500 truncate">
                    {a.service_name} · ${a.price}
                  </div>
                </div>
                <StatusBadge status={a.status} />
              </div>
            ))}
          </div>
        )}
      </div>
    </OwnerLayout>
  );
}
